import { findAdminUser, getEmployeeLoginId, getEmployeeName, getEmployeeRecordId } from "./tecgymAdminAttendance";
import {
  USER_LEAVES_FIELD,
  USER_LEAVES_KEYS,
  USER_WORK_FROM_HOME_FIELD,
  USER_WORK_FROM_HOME_KEYS,
  buildUserActivityLists,
  fetchTecgymAdminUsers,
  readUserListField,
  updateTecgymAdminUserFields
} from "./tecgymAdminUsers";

const REQUEST_KINDS = {
  leave: {
    field: USER_LEAVES_FIELD,
    readKeys: USER_LEAVES_KEYS
  },
  wfh: {
    field: USER_WORK_FROM_HOME_FIELD,
    readKeys: USER_WORK_FROM_HOME_KEYS
  }
};

function getKind(mode) {
  return REQUEST_KINDS[mode] || REQUEST_KINDS.leave;
}

function isLeaveMode(mode) {
  return mode !== "wfh";
}

function readObject(value) {
  if (!value) {
    return {};
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  return typeof value === "object" && !Array.isArray(value) ? value : {};
}

const APPROVER_IDS = ["masood", "shanzay", "ceo"];

export function emptyApprovals() {
  return {
    masood: { status: "Pending", message: "" },
    shanzay: { status: "Pending", message: "" },
    ceo: { status: "Pending", message: "" }
  };
}

function normalizeApprovalStatus(value) {
  const status = String(value || "").trim();
  if (status === "Approved") {
    return "Approved";
  }
  if (status === "Rejected" || status === "Reject") {
    return "Rejected";
  }
  return "Pending";
}

export function normalizeApprovals(approvals, decisions, rejectReasons) {
  const source = readObject(approvals);
  const decisionMap = readObject(decisions);
  const reasonMap = readObject(rejectReasons);
  const normalized = emptyApprovals();

  APPROVER_IDS.forEach((id) => {
    const entry = readObject(source[id]);
    const status = normalizeApprovalStatus(entry.status || decisionMap[id]);
    const legacyMessage = typeof reasonMap[id] === "string" ? reasonMap[id] : "";
    normalized[id] = {
      status,
      message: status === "Rejected" ? String(entry.message || legacyMessage || "").trim() : ""
    };
  });

  return normalized;
}

function countInclusiveDays(fromDate, toDate) {
  if (!fromDate || !toDate || toDate < fromDate) {
    return 0;
  }

  const start = new Date(`${fromDate}T00:00:00`);
  const end = new Date(`${toDate}T00:00:00`);
  return Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;
}

export function normalizeEmployeeRequest(record, mode = "leave") {
  if (!record || typeof record !== "object" || Array.isArray(record)) {
    return null;
  }

  const nested = record.employeeLeave || record.employeeWorkFromHome || record.record || record.data;
  const source = nested && typeof nested === "object" && !Array.isArray(nested) && (nested._id || nested.fromDate || nested.employeeName)
    ? { ...record, ...nested }
    : record;
  const fromDate = String(source.fromDate || source.startDate || "").slice(0, 10);
  const toDate = String(source.toDate || source.endDate || "").slice(0, 10);

  if (!fromDate || !toDate) {
    return null;
  }

  return {
    id: String(source._id || source.id || "").trim(),
    employeeId: String(source.employeeId || source.userId || "").trim(),
    employeeName: String(source.employeeName || source.name || "").trim(),
    employeeEmail: String(source.employeeEmail || source.email || source.loginId || "").trim().toLowerCase(),
    leaveType: isLeaveMode(mode) ? String(source.leaveType || "").trim() : "",
    fromDate,
    toDate,
    dayCount: Number(source.dayCount) || countInclusiveDays(fromDate, toDate),
    reason: String(source.reason || "").trim(),
    status: String(source.status || "Pending").trim() || "Pending",
    decisions: readObject(source.decisions),
    rejectReasons: readObject(source.rejectReasons),
    approvals: normalizeApprovals(source.approvals, source.decisions, source.rejectReasons),
    createdAt: source.createdAt || ""
  };
}

function createRequestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function requestRecordId(record) {
  return String(record?.id || record?._id || "").trim();
}

function sortRequests(records) {
  return [...records].sort((first, second) => {
    const createdCompare = String(second.createdAt || "").localeCompare(String(first.createdAt || ""));
    if (createdCompare !== 0) {
      return createdCompare;
    }

    return String(second.fromDate || "").localeCompare(String(first.fromDate || ""));
  });
}

export function buildEmployeeRequestBody(record, mode) {
  const body = {
    employeeId: record.employeeId || "",
    employeeName: record.employeeName || "",
    employeeEmail: record.employeeEmail || "",
    fromDate: record.fromDate,
    toDate: record.toDate,
    dayCount: record.dayCount,
    reason: record.reason || "",
    status: record.status || "Pending",
    decisions: record.decisions || {},
    rejectReasons: record.rejectReasons || {},
    approvals: normalizeApprovals(record.approvals, record.decisions, record.rejectReasons)
  };

  if (isLeaveMode(mode)) {
    body.leaveType = record.leaveType || "";
  }

  return body;
}

function toStoredRequest(record, mode) {
  return {
    id: requestRecordId(record),
    ...buildEmployeeRequestBody(record, mode),
    createdAt: record.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
}

function requestsFromUser(user, mode) {
  return readUserListField(user, getKind(mode).readKeys)
    .map((item) => normalizeEmployeeRequest({
      ...item,
      employeeId: item?.employeeId || item?.userId || getEmployeeRecordId(user),
      employeeName: item?.employeeName || item?.name || getEmployeeName(user),
      employeeEmail: item?.employeeEmail || item?.email || item?.loginId || getEmployeeLoginId(user)
    }, mode))
    .filter(Boolean);
}

async function saveUserRequests(user, mode, records) {
  const userId = getEmployeeRecordId(user);
  await updateTecgymAdminUserFields(userId, buildUserActivityLists(user, {
    [getKind(mode).field]: records.map((record) => toStoredRequest(record, mode))
  }));
}

export async function fetchEmployeeRequests(mode) {
  const users = await fetchTecgymAdminUsers();
  return sortRequests(users.flatMap((user) => requestsFromUser(user, mode)));
}

export async function createEmployeeRequest(mode, record) {
  const users = await fetchTecgymAdminUsers();
  const user = findAdminUser(users, {
    _id: record?.employeeId,
    email: record?.employeeEmail,
    name: record?.employeeName
  });

  if (!user) {
    throw new Error("Unable to save the request because this employee was not found.");
  }

  const draft = {
    ...record,
    id: requestRecordId(record) || createRequestId(),
    employeeId: record?.employeeId || getEmployeeRecordId(user),
    employeeName: record?.employeeName || getEmployeeName(user),
    employeeEmail: record?.employeeEmail || getEmployeeLoginId(user),
    createdAt: record?.createdAt || new Date().toISOString()
  };
  const existing = requestsFromUser(user, mode).filter((item) => item.id !== draft.id);

  await saveUserRequests(user, mode, [...existing, draft]);
  return draft;
}

export async function updateEmployeeRequest(mode, record) {
  const requestId = requestRecordId(record);
  if (!requestId) {
    throw new Error("This request cannot be updated because it has no saved id.");
  }

  const users = await fetchTecgymAdminUsers();
  const user = findAdminUser(users, {
    _id: record?.employeeId,
    email: record?.employeeEmail,
    name: record?.employeeName
  });

  if (!user) {
    throw new Error("Unable to update the request because this employee was not found.");
  }

  const existing = requestsFromUser(user, mode);
  const current = existing.find((item) => item.id === requestId);
  if (!current) {
    throw new Error("This request was not found on the employee.");
  }

  const nextRecord = {
    ...current,
    ...record,
    id: requestId,
    createdAt: current.createdAt || record.createdAt || ""
  };
  const next = existing.map((item) => (item.id === requestId ? nextRecord : item));

  await saveUserRequests(user, mode, next);
  return nextRecord;
}
